<script setup lang="ts">
import device from '@@/bits/device'
import { isPostPinned } from '@@/bits/post_state'
import { defineAsyncComponent } from '@@/bits/vue'
import { WallWishGroupByTypes } from '@@/enums'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { LogicalEdge, OnDropPayload } from '@@/pragmatic-drag-and-drop/helpers'
import { useDraggableElement } from '@@/pragmatic-drag-and-drop/useDraggableElement'
import { useDropTargetElement } from '@@/pragmatic-drag-and-drop/useDropTargetElement'
import type { Post } from '@@/types'
import SurfacePost from '@@/vuecomponents/surface_post.vue'
import SurfacePostCompressed from '@@/vuecomponents/SurfacePostCompressed.vue'
import SurfacePostDragPreview from '@@/vuecomponents/SurfacePostDragPreview.vue'
import { useSurfacePostDraggableHelper } from '@@/vuecomposables/surface_post_draggable_helper'
import { usePostSorter } from '@@/vuecomposables/surface_post_sorter'
import { usePreferredDark } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const OzDropIndicatorLine = defineAsyncComponent(() => import('@@/library/v4/components/OzDropIndicatorLine.vue'))

const itemEl = ref<HTMLElement | undefined>(undefined)
const props = withDefaults(
  defineProps<{
    post: Post
    dragElementType: string
    dropElementType: string
    axis: 'vertical' | 'horizontal'
    gap: number
    shouldCompress: boolean
    isDraggable: boolean
    groupId: number | string

    // For SurfacePost.vue
    index: number
    postReactionIds: number[]
    postCommentIds: number[]
    putAttachmentOnTop?: boolean
    xShadow?: boolean
    lazyRender?: boolean
    minPostHeight?: number
    minPostWidth?: number
    dragPreviewWidth?: number
  }>(),
  {
    putAttachmentOnTop: false,
    xShadow: true,
    lazyRender: false,
    minPostHeight: 0,
    minPostWidth: 0,
    dragPreviewWidth: undefined,
  },
)

const emit = defineEmits<{
  (e: 'on-drag-start'): void
  (e: 'on-drop', payload: OnDropPayload<{ post: Post }, { post: Post }>): void
}>()

const surfacePostsStore = useSurfacePostsStore()
const { isAPostDragging, groupIdUnderPostBeingDragged } = storeToRefs(useSurfacePostsDragAndDropStore())
const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
const { wishGroupBy, isGroupByColor } = storeToRefs(useSurfaceWishArrangementStore())

// #region Drag and drop for posts
function handlePostDragStart(): void {
  isAPostDragging.value = true
  groupIdUnderPostBeingDragged.value = props.groupId
}
function handlePostDrop(e: OnDropPayload<{ post: Post }, { post: Post }>): void {
  isAPostDragging.value = false
  groupIdUnderPostBeingDragged.value = undefined
  const targetPost = e.targetItem.data.post
  const sourcePost = e.sourceItem.data.post
  const posts = allSortedPostsByGroup.value[props.groupId]
  if (!posts) return
  const targetIndex = posts.findIndex((post) => post.cid === targetPost.cid)
  // Early return if the target post is not in the group
  if (targetIndex === -1) return
  let prevPost: Post | undefined
  let nextPost: Post | undefined
  if (e.relativePositionToTarget === 'before') {
    prevPost = posts[targetIndex - 1]
    nextPost = posts[targetIndex]
  } else {
    prevPost = posts[targetIndex]
    nextPost = posts[targetIndex + 1]
  }

  if (isPostPinned(sourcePost)) {
    // Dropping a pinned post before an unpinned post -> don't take next unpinned post into account
    if (!isPostPinned(nextPost)) {
      nextPost = undefined
    }
  } else {
    // Dropping an unpinned post after a pinned post -> don't take previous pinned post into account
    if (isPostPinned(prevPost)) {
      prevPost = undefined
    }
  }

  const { sortIndex, gapSize } = usePostSorter().getNewIndexBetween(prevPost, nextPost)
  // Given the groupId is a string, we need to convert it to a number. We fallback to null if it's not a number.
  let sectionId = wishGroupBy.value === WallWishGroupByTypes.Section ? Number(props.groupId) : null
  if (Number.isNaN(sectionId)) {
    sectionId = null
  }

  // Update new post order to server and handle color change if grouping by color
  surfacePostsStore.handlePostDropUpdate({
    postCid: sourcePost.cid,
    sortIndex,
    sectionId,
    gapSize,
    color: isGroupByColor.value ? targetPost.color : undefined,
  })

  // Scroll the source post into view after dropping
  // Add setTimeout to ensure the DOM is updated
  setTimeout(() => {
    const element = document.querySelector(`[data-id="${sourcePost.id}"]`)
    element?.scrollIntoView({ behavior: 'instant', block: 'center' })
  }, 0)
}
// #endregion

const itemData = computed<{ post: Post; width?: number }>(() => ({
  post: props.post,
  width: props.dragPreviewWidth,
}))
const elementRef = computed<HTMLElement | undefined>(() => {
  if (itemEl.value === undefined) return undefined
  return itemEl.value
})

const dragPreviewElRef = ref<InstanceType<typeof SurfacePostCompressed> | undefined>(undefined)
const { isDragging } = useDraggableElement({
  elementRef,
  type: props.dragElementType,
  data: itemData,
  dragPreview: SurfacePostDragPreview,
  dragPreviewComponentProps: itemData as any,
  isDraggable: computed(() => props.isDraggable),
  skipSafariPatch: device.ios,
  onDragStart: () => {
    handlePostDragStart()
    emit('on-drag-start')
  },
})

const fixedEdge = ref<LogicalEdge | undefined>(undefined)
const { closestEdgeToPointer, addDraggableSource } = useDropTargetElement({
  elementRef,
  type: props.dropElementType,
  data: itemData,
})

const { canDropPost, getEdgeForDropTargetPost } = useSurfacePostDraggableHelper()
addDraggableSource<{ post: Post }>({
  type: props.dragElementType,
  axis: props.axis,
  onDrag: (e) => {
    const dropTargetPost = (e.self.data.data as any).post
    const sourcePost = (e.source.data.data as any).post
    fixedEdge.value = getEdgeForDropTargetPost(dropTargetPost, sourcePost, props.axis)
  },
  onDragLeave: () => {
    fixedEdge.value = undefined
  },
  canDrop: (e) => {
    const draggedPost = e.sourceItem.data.post
    const targetPost = e.targetItem.data.post
    return canDropPost(draggedPost, targetPost)
  },
  onDrop: (e) => {
    handlePostDrop(e)
    emit('on-drop', e)
    fixedEdge.value = undefined
  },
})

const isDarkMode = usePreferredDark()
const actualEdge = computed(() => fixedEdge.value ?? closestEdgeToPointer.value)

function onDragEnd(): void {
  // If the post is not being dragged, we don't need to remove the drag preview element.
  if (!isAPostDragging.value) return

  // Manually reset the drag and drop state.
  isAPostDragging.value = false
  groupIdUnderPostBeingDragged.value = undefined
}

onMounted(() => {
  if (!itemEl.value) return
  itemEl.value.addEventListener('dragend', onDragEnd)
})

onBeforeUnmount(() => {
  if (!itemEl.value) return
  itemEl.value.removeEventListener('dragend', onDragEnd)
})
</script>

<template>
  <div ref="itemEl" class="flex flex-col relative">
    <SurfacePost
      v-show="!shouldCompress"
      :class="[
        // Exclude this component from using styles in app/assets/stylesheets/3/surface/show/layouts/grid.scss
        'wish-v2',
        isDragging && 'opacity-40',
      ]"
      :index="index"
      :post="post"
      :post-reaction-ids="postReactionIds"
      :post-comment-ids="postCommentIds"
      :put-attachment-on-top="putAttachmentOnTop"
      :x-shadow="xShadow"
      :lazy-render="lazyRender"
      :min-post-height="minPostHeight"
      :min-post-width="minPostWidth"
    />
    <SurfacePostCompressed
      v-if="isDraggable"
      v-show="shouldCompress"
      ref="dragPreviewElRef"
      :post="post"
      :class="[isDragging && 'opacity-40']"
    />
    <OzDropIndicatorLine v-if="actualEdge" :edge="actualEdge" :gap="gap" :dark-mode="isDarkMode" class="mx-1" />
  </div>
</template>
